// Pick a client invoice by searching for it.
//
// Extracted from CreditNoteDetailPage, which had the search inline: an input
// that fetched on every keystroke and rendered a list ONLY when the server
// returned rows. Three things followed from that, all of which read as "there is
// no dropdown here":
//
//   - nothing opened until you typed, so an invoice you could not name was
//     unreachable — there was no way to browse what exists;
//   - a query with no matches rendered nothing at all, which is indistinguishable
//     from a broken box;
//   - no keyboard, no outside-click close, and overlapping responses could land
//     out of order and repopulate a list you had already moved past.
//
// This opens on focus with the most recent invoices, filters as you type, and
// says so when it is loading or has found nothing. Search breadth is the
// server's (`listClientInvoices`) — invoice number, client, vessel, PO number,
// RFQ/quotation number, and the vendors that supplied the lines.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ClientInvoiceDto, fetchClientInvoices } from '../lib/clientInvoiceApi';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { formatDate as fmtDate } from '../utils/dateFormat';

const RESULT_LIMIT = 8;

interface InvoiceSearchSelectProps {
  onSelect: (invoice: ClientInvoiceDto) => void;
  /** Already-picked invoice ids — shown as picked and not selectable twice. */
  selectedIds?: string[];
  placeholder?: string;
  disabled?: boolean;
}

function money(value: number) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  } catch {
    return `USD ${value.toFixed(2)}`;
  }
}

/** The vendors behind an invoice's lines, deduped — the reason a vendor name finds it. */
function vendorsOf(inv: ClientInvoiceDto): string {
  const names = new Set<string>();
  (inv.items || []).forEach((it: any) => {
    (it?.sourceProvenance || []).forEach((p: any) => {
      if (p?.vendorName) names.add(String(p.vendorName));
    });
    if (it?.supplierName) names.add(String(it.supplierName));
    // H10 is a rendered string ("S / P / Vendor1, Vendor2"), not a list — the
    // codes in front of it are sources, not suppliers, so only the tail is a name.
    String(it?.sourceSupplierNames || '')
      .split(/[,/]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 1 && !['S', 'P', 'O'].includes(s.toUpperCase()))
      .forEach((s) => names.add(s));
  });
  return Array.from(names).slice(0, 4).join(', ');
}

export const InvoiceSearchSelect: React.FC<InvoiceSearchSelectProps> = ({
  onSelect,
  selectedIds = [],
  placeholder = 'Search by invoice number, client, vessel, PO or vendor…',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ClientInvoiceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();
  const beginRequest = useLatestRequest();

  // An empty query is a real search here — it asks for the most recent invoices,
  // which is what makes the list browsable before you know what to type.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const isCurrent = beginRequest();
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetchClientInvoices(1, RESULT_LIMIT, query.trim(), 'all');
        if (cancelled || !isCurrent()) return;
        setResults(res.clientInvoices || []);
        setHighlight(0);
      } catch {
        if (!cancelled && isCurrent()) setResults([]);
      } finally {
        // Guarded too: a superseded response clearing this would turn the
        // spinner off for a request that is still running.
        if (!cancelled && isCurrent()) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, beginRequest]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = useCallback(
    (inv: ClientInvoiceDto) => {
      if (selectedIds.includes(inv._id)) return;
      onSelect(inv);
      setQuery('');
      setOpen(false);
    },
    [onSelect, selectedIds]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const inv = results[highlight];
      if (inv) choose(inv);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={listboxId}
        className="w-full h-9 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-primary-500 disabled:bg-slate-50"
      />

      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {!query.trim() && !loading && results.length > 0 && (
            <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
              Most recent
            </div>
          )}

          {loading && <div className="px-3 py-3 text-xs text-slate-500">Searching…</div>}

          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {query.trim()
                ? `No invoice matches “${query.trim()}”. Try the client, vessel, PO number or a vendor.`
                : 'No client invoices yet.'}
            </div>
          )}

          {!loading &&
            results.map((inv, i) => {
              const picked = selectedIds.includes(inv._id);
              const vendors = vendorsOf(inv);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  key={inv._id}
                  disabled={picked}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => choose(inv)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 ${
                    picked ? 'opacity-50 cursor-default' : i === highlight ? 'bg-slate-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-slate-800">{inv.invoiceNumber}</span>
                    <span className="text-slate-500">
                      {picked ? 'Added' : money(Number(inv.summary?.invoiceGrandTotal || 0))}
                    </span>
                  </div>
                  <div className="text-slate-500">
                    {inv.clientName || '--'} • {inv.vesselName || '--'}
                    {inv.clientPoNumber ? ` • PO ${inv.clientPoNumber}` : ''}
                    {inv.invoiceDate ? ` • ${fmtDate(inv.invoiceDate, '')}` : ''}
                  </div>
                  {vendors && <div className="text-slate-400 truncate">via {vendors}</div>}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default InvoiceSearchSelect;
